// ============================================================================
// TENANT APPLICATION ASSIGNMENT API TYPES
// ============================================================================
// Cross-tenant view of the tenant ↔ application assignment relationship.
// Matches tenant-hub-api DTOs:
//   - TenantAppAssignmentDto.cs
//   - TenantAppAssignmentSearchRequest.cs
//   - TenantAppAssignmentStatistics.cs

import type { ID } from './common.types';
import type { TenantHubPagedResult, TenantHubPagedRequest } from './tenants.types';

// ============================================================================
// ENUMS
// ============================================================================

export type AssignmentStatus = 'Active' | 'Trial' | 'Suspended' | 'Expired';

export type SubscriptionPlan = 'Free' | 'Standard' | 'Pro' | 'Enterprise';

export type BillingCycle = 'monthly' | 'annual';

export type HealthStatus = 'healthy' | 'degraded' | 'down' | 'pending_db';

export type DbProvider = 'postgresql' | 'mysql' | 'sqlserver' | 'oracle';

// ============================================================================
// JOINED RESPONSE — drives the cross-tenant master grid
// ============================================================================

/**
 * Flat joined view of a tenant ↔ application assignment.
 * Matches backend TenantAppAssignmentDto.cs.
 * Secrets (db_password, keycloak_client_secret) are intentionally NOT exposed.
 */
export interface TenantAppAssignment {
  id: ID;

  // Tenant identity
  tenantId: ID;
  tenantCode: string;
  tenantName: string;

  // Application identity
  appId: ID;
  appCode: string;
  appName: string;
  appVersion?: string;
  tenantAppVersion?: string;

  // Subscription
  subscriptionPlanId?: ID;
  subscriptionPlanName?: string;
  billingCycle: BillingCycle | string;
  subscriptionStartDate?: string;
  subscriptionEndDate?: string;
  billingEmail?: string;
  nextBillingDate?: string;

  // Trial
  isTrial: boolean;
  trialEndDate?: string;

  // Limits
  maxUsers: number;
  maxStorageGb: number;
  apiRateLimit: number;

  // State
  isSuspended: boolean;
  suspensionReason?: string;
  suspendedAt?: string;
  healthStatus: HealthStatus | string;
  maintenanceMode: boolean;

  // Keycloak (no secret)
  keycloakClientId?: string;
  callbackUrl?: string;
  logoutUrl?: string;

  // Database (no password)
  dbProvider?: DbProvider | string;
  dbName?: string;
  dbHost?: string;
  dbPort?: number;
  dbUsername?: string;
  connectionString?: string;

  // Status
  statusId?: ID;
  statusCode?: string;
  statusName?: string;

  /** Computed by SQL: Active / Trial / Suspended / Expired */
  derivedStatus: AssignmentStatus;

  installedDate: string;
  lastAccessDate?: string;

  createdAt: string;
  createdBy?: ID;
  updatedAt: string;
  updatedBy?: ID;
}

// ============================================================================
// CREATE / UPDATE REQUESTS — sent to per-tenant CRUD endpoints
// ============================================================================

/**
 * Payload for POST /api/v1/tenants/{tenantId}/applications.
 * The frontend constructs this from the multi-section form modal.
 */
export interface CreateTenantApplicationRequest {
  // Application — appName/appCode are [Required] on the backend DTO
  // (CreateTenantApplicationRequest.cs); appCode drives the duplicate check
  appId: ID;
  appName: string;
  appCode: string;
  appVersion?: string;

  // Keycloak client
  keycloakClientId?: string;
  keycloakClientSecret?: string;
  callbackUrl?: string;
  logoutUrl?: string;

  // Database (optional — admin can defer)
  dbProvider?: DbProvider | string;
  dbName?: string;
  dbHost?: string;
  dbPort?: number;
  dbUsername?: string;
  dbPassword?: string;
  /** Full provider connection string — persisted to tbl_tenant_application.connection_string */
  connectionString?: string;

  // Subscription
  subscriptionPlanId?: ID;
  subscriptionPlanName?: SubscriptionPlan | string;
  billingCycle?: BillingCycle | string;
  subscriptionStartDate?: string;
  subscriptionEndDate?: string;
  billingEmail?: string;

  // Trial
  isTrial?: boolean;
  trialEndDate?: string;

  // Limits
  maxUsers?: number;
  maxStorageGb?: number;
  apiRateLimit?: number;
  featureFlags?: Record<string, unknown>;
}

/**
 * Payload for PUT /api/v1/tenants/{tenantId}/applications/{applicationId}.
 * The route addresses the row by its ORIGINAL application id; setting `appId`
 * in the body moves the assignment to a different application (backend rejects
 * a target app that is already assigned to the tenant).
 */
export interface UpdateTenantApplicationRequest extends Omit<CreateTenantApplicationRequest, 'appId'> {
  /** Optional: move the assignment to a different application. */
  appId?: ID;
  isSuspended?: boolean;
  suspensionReason?: string;
}

// ============================================================================
// SEARCH REQUEST — sent to GET /api/v1/tenant-app-assignments
// ============================================================================

/**
 * Matches backend TenantAppAssignmentSearchRequest.cs.
 * Note: backend uses `page`/`pageSize` (NOT `pageNumber`).
 */
export interface TenantAppAssignmentSearchRequest {
  searchTerm?: string;
  tenantIds?: ID[];
  applicationIds?: ID[];
  planNames?: string[];
  statuses?: AssignmentStatus[];
  billingCycles?: string[];
  healthStatuses?: string[];
  trialEndingFrom?: string;
  trialEndingTo?: string;
  subscriptionEndingFrom?: string;
  subscriptionEndingTo?: string;
  installedFrom?: string;
  installedTo?: string;
  includeSuspended?: boolean;
  includeRemoved?: boolean;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// ============================================================================
// STATISTICS — drives the stats banner
// ============================================================================

export interface TenantAppAssignmentStatistics {
  total: number;
  active: number;
  trial: number;
  suspended: number;
  expired: number;
  expiringWithin30Days: number;
}

// ============================================================================
// CONSTANTS
// ============================================================================

export const SUBSCRIPTION_PLANS: SubscriptionPlan[] = ['Free', 'Standard', 'Pro', 'Enterprise'];

export const BILLING_CYCLES: BillingCycle[] = ['monthly', 'annual'];

export const HEALTH_STATUSES: HealthStatus[] = ['healthy', 'degraded', 'down', 'pending_db'];

export const ASSIGNMENT_STATUSES: AssignmentStatus[] = ['Active', 'Trial', 'Suspended', 'Expired'];

// Re-export shared paged result type for callers that import everything via this module
export type { TenantHubPagedResult, TenantHubPagedRequest };
